
import type { BrowseConfigurationAttributeValue } from '@empathybroker/tsclient-play/dist/models/browse-configuration-attribute-value'
import type { PropType } from 'vue/types/options'

import type { AuditDetailField, AuditItem } from '../../../models'
import { deepEqual } from '@empathyco/x-utils'
import { defineComponent } from 'vue'

import { TYPES } from '../../../config/inversify.types'

import RankingElementsListItem from '../../configurations/ranking-elements-list-item.component.vue'

import AuditDetails from './audit-details.component.vue'

enum ConfigurationAuditDetailValuesEnum {
  PRODUCTS = 'products',
  ATTRIBUTES = 'attributes',
  FACETS = 'facets',
}

interface DetailElement {
  id: string
  previousValue?: any
  nextValue?: any
}

interface ChangingElements {
  created: any[]
  updated: any[]
  deleted: any[]
  unmodified: any[]
}

export default defineComponent({
  name: 'ConfigurationAuditDetails',
  components: {
    AuditDetails,
    RankingElementsListItem,
  },
  inject: {
    objectIdService: TYPES.ObjectIdService,
  },
  props: {
    audit: {
      type: Object as PropType<AuditItem>,
      required: true,
    },
    ignoredFields: {
      type: Array as PropType<Array<string>>,
      default: () =>
        AuditDetails.props.ignoredFields
          .default()
          .concat(['ranking', 'products', 'attributes', 'facets']),
    },
    messages: {
      type: Object,
      required: true,
    },
  },
  methods: {
    getChangingElementsText(elementsDetailField: AuditDetailField): string {
      return Object.entries(this.getChangingElements(elementsDetailField))
        .reduce(
          (
            changingElementsTexts: string[],
            [changingElementsKey, changingElementsValues]: [string, any[]],
          ) => {
            if (changingElementsKey !== 'unmodified' && !!changingElementsValues.length) {
              changingElementsTexts.push(
                this.$epb.utils.compileMessage(
                  this.messages.changingElements[changingElementsKey],
                  {
                    count: changingElementsValues.length,
                  },
                ),
              )
            }
            return changingElementsTexts
          },
          [],
        )
        .join(', ')
    },
    getChangingElements(elementsDetailField: AuditDetailField): ChangingElements {
      return this.getElements(elementsDetailField).reduce(
        (changingElements: ChangingElements, element: DetailElement) => {
          if (!element.previousValue) {
            changingElements.created.push(element)
          } else if (!element.nextValue) {
            changingElements.deleted.push(element)
          } else if (!deepEqual(element.previousValue, element.nextValue)) {
            changingElements.updated.push(element)
          } else {
            changingElements.unmodified.push(element)
          }
          return changingElements
        },
        {
          created: [],
          updated: [],
          deleted: [],
          unmodified: [],
        },
      )
    },
    getElements(elementsDetailField: AuditDetailField): DetailElement[] {
      const elements = []
      const previousElements = elementsDetailField.previousValue || []
      previousElements.forEach(previousElement => {
        elements.push({
          id: this.getElementId(previousElement),
          previousValue: previousElement,
        })
      })
      const nextElements = elementsDetailField.nextValue || []
      nextElements.forEach(nextElement => {
        const nextElementId = this.getElementId(nextElement)
        const existingElement = elements.find(element => element.id === nextElementId)
        if (existingElement) {
          existingElement.nextValue = nextElement
        } else {
          elements.push({
            id: nextElementId,
            nextValue: nextElement,
          })
        }
      })
      return elements
    },
    getElementId(element: any): string {
      return element.id || `${element.fieldName}.${element.fieldValue}`
    },
    getAttributeValuesText(attributeValues: BrowseConfigurationAttributeValue[]): string {
      return attributeValues
        .map((value: BrowseConfigurationAttributeValue) => `${value.name} (${value.id})`)
        .join(', ')
    },
    isAttribute(parentId: string): boolean {
      return parentId === ConfigurationAuditDetailValuesEnum.ATTRIBUTES
    },
    isFacet(parentId: string): boolean {
      return parentId === ConfigurationAuditDetailValuesEnum.FACETS
    },
  },
})
